import { Banknote, Building2, LineChart, Receipt, ScanLine, Users } from 'lucide-react';
import { isSubscriptionCost } from '@/features/licenses/CostRecordDimensions';
import { formatCount, formatPercent, shortUser } from '@/shared/analytics/MetricFormatting';
import { formatSignalValue } from '@/shared/signals/SignalFormatting';
import { aggregateSeriesLoader, trendRatioLoader } from '@/shared/signals/SignalLiveLoaders';
import { kpiSample, seriesSample, trendSample } from '@/shared/signals/SignalSampleData';
import type { SignalData, SignalTileDef } from '@/shared/signals/SignalTypes';

const DEPARTMENT_LABELS = ['Engineering', 'Sales', 'Marketing', 'Finance', 'Legal', 'Support'] as const;
const SPENDER_LABELS = ['j.chen', 'm.patel', 's.kim', 'a.ross', 't.wu', 'r.diaz'] as const;
const COHORT_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'] as const;

/**
 * The Token Usage landing dashboard. Interactions now carry `allocated_cost` —
 * real consumption spend distributed by token share at ETL publish time — so
 * the Department Cost tile charts actual dollars (`allocatedCostUsd`), falling
 * back to its illustrative sample until cost coverage lands. Token volume still
 * backs the volume-shaped tiles: department footprint, the context-stuffing
 * ratio, and the top-spender distribution are live from the analytics
 * OLAP gateway. Cost efficiency is live too, as the token cost-proxy per unit of
 * work month over month (totalTokens ÷ turns) — the "spend less per unit of work
 * over time" line, where lower is the win. Model-to-task fit is live from the
 * `license_costs` feed joined to telemetry tokens: per-model effective $/token
 * classifies a premium tier, then the gauge is premium-model tokens spent on
 * low-difficulty work. It needs at least two priced models to define the tier, so
 * it stays illustrative until cost coverage spans more than one model (only the
 * providers that report cost — Anthropic, OpenAI — populate it today). Failed-prompt
 * overhead is live too: re-prompt restarts (near-identical prompts re-submitted
 * within minutes, from the `interaction_text` silver source) priced at each
 * model's effective $/token from the cost feed — the rework cost of failed asks.
 * It needs prompt content + cost coverage, so it stays illustrative when either is
 * absent. License ROI is live too — flat seat/subscription cost vs metered usage
 * cost, both read from the `license_costs` feed split by `cost_type` (under 100%
 * means seats beat metered). It falls back to illustrative when no subscription
 * cost is recorded, or usage is too light for the ratio to be meaningful.
 */
export const TOKEN_USAGE_SIGNALS: SignalTileDef[] = [
    {
        id: 'cost-by-department',
        title: 'Cost by Department',
        description: 'The spend footprint of each team, ranked by token volume as the cost proxy. Open for the full field of departments and their people.',
        icon: Building2,
        accent: 'teal',
        kind: 'bars',
        to: '/cost/by-department',
        sample: (rng) => seriesSample(rng, DEPARTMENT_LABELS, { min: 80_000, max: 1_400_000, format: 'compact' }),
        live: aggregateSeriesLoader({ groupBy: 'user_department', metric: 'totalTokens', format: 'compact', emptyMessage: 'no department spend to chart' }),
    },
    {
        id: 'department-allocated-cost',
        title: 'Department Cost ($)',
        description: 'Actual allocated spend per team — real provider cost distributed across interactions by token share. Open for the full ranked field.',
        icon: Banknote,
        accent: 'teal',
        kind: 'bars',
        to: '/cost/by-department',
        sample: (rng) => seriesSample(rng, DEPARTMENT_LABELS, { min: 200, max: 18_000, format: 'currency' }),
        live: async ({ gateway, filters }): Promise<SignalData> => {
            const res = await gateway.aggregate({
                source: 'interactions',
                groupBy: ['user_department'],
                metrics: ['allocatedCostUsd'],
                orderBy: { metric: 'allocatedCostUsd', direction: 'desc' },
                filters,
                limit: 6,
            });
            const series = res.rows
                .filter((row) => row.metrics.allocatedCostUsd > 0)
                .map((row) => ({ label: row.keys['user_department'] ?? '—', value: row.metrics.allocatedCostUsd }));
            if (series.length === 0) throw new Error('no allocated cost recorded for this window yet — the sample stays');
            return { series, format: 'currency' };
        },
    },
    {
        id: 'data-to-output-efficiency',
        title: 'Data to Output Efficiency',
        description: 'Input tokens spent per output token produced — a read on context stuffing, where lower is leaner.',
        icon: Receipt,
        accent: 'amber',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 1.5, max: 8, format: 'ratio', caption: 'input ÷ output tokens', goodWhen: 'down', spark: 'flat' }),
        live: async ({ gateway, filters }): Promise<SignalData> => {
            const res = await gateway.aggregate({ source: 'interactions', groupBy: [], metrics: ['inputTokens', 'outputTokens'], filters });
            const output = res.totals.outputTokens;
            if (output <= 0) throw new Error('no output tokens to ratio against');
            const ratio = res.totals.inputTokens / output;
            return { headline: formatSignalValue('ratio', ratio), format: 'ratio', caption: 'input ÷ output tokens' };
        },
    },
    {
        id: 'top-spenders',
        title: 'Top Spenders',
        description: 'How token expenditure concentrates across individual users — the heaviest consumers of the budget.',
        icon: Users,
        accent: 'violet',
        kind: 'bars',
        to: '/token-usage/spend-top-consumers',
        sample: (rng) => seriesSample(rng, SPENDER_LABELS, { min: 40_000, max: 600_000, format: 'compact' }),
        live: aggregateSeriesLoader({ groupBy: 'user_id', metric: 'totalTokens', format: 'compact', label: shortUser, emptyMessage: 'no per-user spend to chart' }),
    },
    {
        id: 'cost-efficiency',
        title: 'Cost Efficiency',
        description: 'Whether successive onboarding cohorts learn to spend less per unit of work over time — a downward line is the win.',
        icon: LineChart,
        accent: 'rose',
        kind: 'trend',
        to: '/token-usage/spend-trend',
        sample: (rng) => trendSample(rng, COHORT_LABELS, 40, 180, 'down'),
        live: trendRatioLoader({ bucket: 'month', numerator: 'totalTokens', denominator: 'turns', format: 'compact', emptyMessage: 'no cost-efficiency trend to chart' }),
    },
    {
        id: 'license-roi',
        title: 'License ROI',
        description: 'Flat per-seat licensing cost expressed against the equivalent metered API spend — under 100% means seats are the cheaper path.',
        icon: Banknote,
        accent: 'cyan',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 0.4, max: 0.85, format: 'percent', caption: 'seat cost vs metered equivalent', deltaPct: [-12, 6], goodWhen: 'up' }),
        live: async ({ licenses, tuning, range }): Promise<SignalData> => {
            const records = await licenses.getCostRecords(range.start, range.end);

            const currency = records.find((r) => r.currency.length > 0)?.currency ?? 'USD';
            let seatCost = 0;
            let usageCost = 0;
            for (const record of records) {
                if (record.currency !== currency) continue;
                if (isSubscriptionCost(record)) seatCost += record.amount;
                else usageCost += record.amount;
            }
            if (seatCost <= 0) throw new Error('no seat/subscription cost recorded');
            if (usageCost <= 0) throw new Error('no metered usage cost to compare against');

            const ratio = seatCost / usageCost;
            if (ratio > tuning.licenseRoiMaxRatio) throw new Error('usage too light for a meaningful license ROI');
            return { headline: formatPercent(ratio, 0), format: 'percent', caption: 'seat cost vs metered equivalent' };
        },
    },
    {
        id: 'failed-prompt-overhead',
        title: 'Failed Prompt Overhead',
        description: 'The capital burned on failed prompts and the retries they trigger — pure rework with no delivered value.',
        icon: ScanLine,
        accent: 'indigo',
        kind: 'kpi',
        sample: (rng) => kpiSample(rng, { min: 1500, max: 12000, format: 'currency', caption: 'monthly retry/rework waste', deltaPct: [-18, 10], goodWhen: 'down', spark: 'down' }),
        live: async ({ gateway, licenses, filters, range, windowDays }): Promise<SignalData> => {
            const [overhead, agg, costRecords] = await Promise.all([
                gateway.rePromptOverhead({ filters }),
                gateway.aggregate({ source: 'interactions', groupBy: ['model'], metrics: ['totalTokens'], filters }),
                licenses.getCostRecords(range.start, range.end),
            ]);

            const costByModel = new Map<string, number>();
            for (const record of costRecords) {
                if (record.modelId === undefined || record.modelId.length === 0) continue;
                costByModel.set(record.modelId, (costByModel.get(record.modelId) ?? 0) + record.amount);
            }
            const tokensByModel = new Map<string, number>();
            for (const row of agg.rows) {
                const model = row.keys.model;
                if (model === undefined || model === '—') continue;
                tokensByModel.set(model, (tokensByModel.get(model) ?? 0) + row.metrics.totalTokens);
            }

            let wasted = 0;
            let retries = 0;
            for (const row of overhead) {
                retries += row.retries;
                const cost = costByModel.get(row.model);
                const tokens = tokensByModel.get(row.model);
                if (cost === undefined || tokens === undefined || tokens <= 0) continue;
                wasted += row.wastedTokens * (cost / tokens);
            }
            if (wasted <= 0) throw new Error('no priced retry overhead to chart');
            return {
                headline: formatSignalValue('currency', wasted),
                format: 'currency',
                caption: `${formatCount(retries)} retries · last ${windowDays}d`,
            };
        },
    },
];
